import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Search,
  Edit,
  Trash2,
  Copy,
  ArrowLeft,
  Loader2,
  FileSignature
} from 'lucide-react';

const ContractTemplates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/contract-templates');
      console.log('Templates API Response:', res.data);
      console.log('Response has templates:', res.data.templates !== undefined);
      console.log('Template count:', res.data.templates?.length || 0);
      
      const templateList = res.data.templates || [];
      setTemplates(templateList);
      
      if (templateList.length > 0) {
        console.log('First template:', templateList[0]);
        toast.success(`Loaded ${templateList.length} templates`);
      } else {
        console.warn('No templates returned from API');
      }
    } catch (error) {
      console.error('Template fetch error:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to load templates: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await api.delete(`/contract-templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      contract: 'bg-blue-100 text-blue-700 border-blue-300',
      work_order: 'bg-purple-100 text-purple-700 border-purple-300',
      project_document: 'bg-green-100 text-green-700 border-green-300',
      amendment: 'bg-orange-100 text-orange-700 border-orange-300'
    };
    return colors[category] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/contracts')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Contracts
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Contract Templates</h1>
              <p className="text-sm text-slate-500 mt-1">
                Create and manage reusable contract templates
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/contracts/templates/new')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Templates List */}
      {filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 mb-4">
              {searchTerm ? 'No templates match your search' : 'No templates yet'}
            </p>
            {!searchTerm && (
              <p className="text-sm text-slate-400 mt-2">
                Contact your administrator to add templates
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileSignature className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-slate-800">{template.name}</h3>
                  </div>
                  <Badge className={getCategoryBadge(template.category)}>
                    {template.category.replace('_', ' ')}
                  </Badge>
                </div>

                {template.description && (
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                    {template.description}
                  </p>
                )}

                {template.fields && template.fields.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-slate-500 mb-1">Fields:</p>
                    <div className="flex flex-wrap gap-1">
                      {template.fields.slice(0, 3).map((field, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {field.name || field}
                        </Badge>
                      ))}
                      {template.fields.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{template.fields.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/contracts/new?template=${template.id}`)}
                    className="flex-1"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Use
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/contracts/templates/${template.id}/edit`)}
                  >
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(template.id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractTemplates;
